/**
 * FestivalPitchDeck — print-optimized one-pager for sales meetings.
 *
 * Routes: /admin/festivals/:slug/pitch
 *
 * Renders a clean, brandable one-pager that the operator can take
 * into a sponsor meeting (NBD, Digicel, Republic Bank, etc.).
 * Designed to print to PDF cleanly with one click. Fits a single
 * A4 page in landscape orientation.
 *
 * What's on the deck
 * ──────────────────
 *   - Festival hero banner (theme color + emoji + name + dates + venue)
 *   - One-paragraph elevator pitch (the festival's `description`)
 *   - Available sponsorship tiers as a pricing table
 *   - "What you get" bullet list per tier (the `perks` array)
 *   - Total addressable revenue calculation
 *   - Contact line for the H-Plate sales team
 *
 * Print behavior
 * ──────────────
 * Uses media-print CSS to:
 *   - Strip the admin chrome (header, sidebar)
 *   - Force light backgrounds for ink-friendly printing
 *   - Hide the "Print" / "Back" action buttons
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'wouter';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Printer, CalendarDays, MapPin, Sparkles, Crown, Mic,
  Route, Pin, Moon, Loader2,
} from 'lucide-react';
import type { FestivalEntry, FestivalTierKey } from '@/stores/campaignWizardStore';

// ── Helpers ───────────────────────────────────────────────────────────────

const TIER_ICON: Record<FestivalTierKey, any> = {
  title: Crown,
  stage: Mic,
  corridor: Route,
  venue_partner: Pin,
  after_party: Moon,
  sub_event: Mic,
};

const TIER_LABEL: Record<FestivalTierKey, string> = {
  title: 'Title sponsor',
  stage: 'Stage sponsor',
  corridor: 'Corridor sponsor',
  venue_partner: 'Venue partner',
  after_party: 'After-party sponsor',
  sub_event: 'Sub-event sponsor',
};

function fmtDateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const fmt: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    return `${s.toLocaleDateString('en-US', fmt)} – ${e.getDate()}, ${e.getFullYear()}`;
  }
  return `${s.toLocaleDateString('en-US', fmt)} – ${e.toLocaleDateString('en-US', { ...fmt, year: 'numeric' })}`;
}

function fmtMoney(n: number): string {
  return `EC$${Math.round(n).toLocaleString()}`;
}

// ── Component ─────────────────────────────────────────────────────────────

export default function FestivalPitchDeck() {
  const params = useParams<{ slug: string }>();
  const slug = params?.slug;

  const [festival, setFestival] = useState<FestivalEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        // Fetch festival via admin_list_festivals + filter by slug.
        // (We don't have a single-festival RPC yet — keep it simple.)
        const { data, error: rpcErr } = await supabase.rpc('admin_list_festivals', {
          p_horizon_days: 1825,
          p_include_past: true,
        });
        if (rpcErr) throw rpcErr;
        const env = data as { ok?: boolean; festivals?: FestivalEntry[] };
        const found = env?.festivals?.find((f) => f.slug === slug) ?? null;
        if (!alive) return;
        setFestival(found);
        if (!found) setError('Festival not found.');
      } catch (err: any) {
        if (alive) setError(err.message || 'Failed to load festival');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [slug]);

  // Total addressable revenue — sum of ALL slots × midpoint of price band.
  const totalAddressable = (() => {
    if (!festival) return 0;
    return festival.sponsorship_tiers.reduce((acc, t) => {
      const midBand = (t.rate_xcd_min + t.rate_xcd_max) / 2;
      return acc + midBand * t.slot_count;
    }, 0);
  })();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error || !festival) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-rose-600 mb-3">{error || 'Festival not found.'}</p>
          <Link href="/admin/festivals">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to festivals
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Print-only CSS — hide admin chrome and force light theme */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 12mm; }
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-page { box-shadow: none !important; border: 0 !important; }
        }
      `}</style>

      <div className="min-h-screen bg-gray-100 dark:bg-gray-950 p-4 md:p-8">
        {/* Action bar (not printed) */}
        <div className="no-print max-w-5xl mx-auto mb-4 flex items-center justify-between">
          <Link href="/admin/festivals">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to festivals
            </Button>
          </Link>
          <Button
            onClick={() => window.print()}
            size="sm"
            className="bg-purple-600 hover:bg-purple-500 text-white"
          >
            <Printer className="w-4 h-4 mr-2" /> Print to PDF
          </Button>
        </div>

        {/* Page */}
        <div className="print-page max-w-5xl mx-auto bg-white text-gray-900 rounded-xl shadow-2xl border border-gray-200 overflow-hidden">

          {/* Hero */}
          <div
            className="px-10 py-8 text-white relative"
            style={{
              background: festival.hero_image_url
                ? `linear-gradient(135deg, ${festival.theme_color}cc, ${festival.theme_color}99 60%, transparent), url(${festival.hero_image_url}) center/cover`
                : `linear-gradient(135deg, ${festival.theme_color}, ${festival.theme_color}aa)`,
            }}
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="text-3xl">{festival.emoji ?? '🎉'}</span>
              <span className="text-xs uppercase tracking-[0.2em] opacity-80 font-semibold">
                Sponsorship opportunity
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black leading-tight">{festival.name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-sm opacity-95">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="w-4 h-4" />
                {fmtDateRange(festival.starts_at, festival.ends_at)}
              </span>
              {festival.venue_name && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" />
                  {festival.venue_name}
                </span>
              )}
              {festival.organizer_name && (
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4" />
                  {festival.organizer_name}
                </span>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="p-8 md:p-10 space-y-7">

            {/* Pitch paragraph */}
            {festival.description && (
              <section>
                <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                  {festival.description}
                </p>
              </section>
            )}

            {/* Reach + audience strip — informational only, populated from
                aggregate platform stats once we wire them in. For the
                initial pitch deck we lean on the festival metadata only. */}
            <section className="grid grid-cols-3 gap-4">
              <Stat
                label="Festival window"
                value={`${Math.max(1, Math.ceil((new Date(festival.ends_at).getTime() - new Date(festival.starts_at).getTime()) / 86400000))} days`}
              />
              <Stat
                label="Sub-events"
                value={String(festival.sub_events.length || '–')}
              />
              <Stat
                label="Sponsorship tiers"
                value={String(festival.sponsorship_tiers.length)}
              />
            </section>

            {/* Sponsorship tiers table */}
            <section>
              <h2 className="text-xs uppercase tracking-[0.2em] text-gray-500 font-bold mb-3">
                Available sponsorship tiers
              </h2>
              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                      <th className="px-4 py-2 font-semibold">Tier</th>
                      <th className="px-4 py-2 font-semibold">Slots</th>
                      <th className="px-4 py-2 font-semibold">Investment (XCD)</th>
                      <th className="px-4 py-2 font-semibold">What you get</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {festival.sponsorship_tiers.map((t) => {
                      const Icon = TIER_ICON[t.tier as FestivalTierKey] ?? Crown;
                      return (
                        <tr key={t.tier} className="align-top">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 font-semibold text-gray-900">
                              <Icon
                                className="w-4 h-4"
                                style={{ color: festival.theme_color }}
                              />
                              {TIER_LABEL[t.tier as FestivalTierKey] ?? t.label}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-700 font-mono">
                            {t.slot_count}
                          </td>
                          <td className="px-4 py-3 font-mono text-gray-900 font-semibold whitespace-nowrap">
                            {fmtMoney(t.rate_xcd_min)}–{fmtMoney(t.rate_xcd_max)}
                          </td>
                          <td className="px-4 py-3">
                            <ul className="space-y-1 text-gray-700 text-xs">
                              {(t.perks || []).map((perk: string, i: number) => (
                                <li key={i} className="flex items-start gap-1.5">
                                  <Sparkles
                                    className="w-3 h-3 mt-0.5 shrink-0"
                                    style={{ color: festival.theme_color }}
                                  />
                                  {perk}
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Total addressable */}
            <section className="rounded-xl bg-gradient-to-br from-emerald-50 to-purple-50 border border-emerald-200 px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-semibold">
                  Total addressable for this festival
                </div>
                <div className="text-2xl font-black text-gray-900 mt-1">
                  ~{fmtMoney(totalAddressable)}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Sum of all slots × midpoint of price band
                </div>
              </div>
              <div className="text-xs text-gray-600 max-w-xs">
                Lock in your tier early. Most slots fill 60+ days
                before the festival window opens.
              </div>
            </section>

            {/* Footer */}
            <footer className="pt-4 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
              <div>
                <strong className="text-gray-700">H-Plate</strong> · Transit-First
                Operating System for the Commonwealth of Dominica
              </div>
              <div>
                Sponsorship enquiries:{' '}
                <a
                  href="mailto:partnerships@h-plate.com"
                  className="text-purple-700 underline"
                >
                  partnerships@h-plate.com
                </a>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Stat ──────────────────────────────────────────────────────────────────

interface StatProps { label: string; value: string }

function Stat({ label, value }: StatProps) {
  return (
    <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
        {label}
      </div>
      <div className="text-lg font-bold text-gray-900 mt-0.5">{value}</div>
    </div>
  );
}
